import { useDispatch } from 'react-redux';
import { toast } from '../../../util/toast';
import { isServerError } from '../../../util/helpers';
import { useRefundPaylinkOrderTransactionMutation } from '../../../store/modules/orders/api';
import { RefundPaylinkTransactionBody } from '../../../store/modules/paylink/types';
import { paylinkApi } from '../../../store/modules/paylink/api';

type UseRefundOrderProps = {
  orderId: string;
  paylinkId: string;
  merchantId: string;
};

export const useRefundPaylink = ({ paylinkId, orderId, merchantId }: UseRefundOrderProps) => {
  const [refundPaylinkTransaction, { isLoading }] = useRefundPaylinkOrderTransactionMutation();
  const dispatch = useDispatch();

  const onRefundPaylink = async (
    refunds: { amount?: number; transactionId: string }[],
    isSplitPayment: boolean,
  ) => {
    const results = await Promise.allSettled(
      refunds.map(async (refund) => {
        const body: RefundPaylinkTransactionBody = {
          paylinkId,
          orderId,
          merchantId,
          amount: refund.amount,
        };
        if (isSplitPayment) {
          body.transactionId = refund.transactionId;
        }
        const response = await refundPaylinkTransaction(body).unwrap();
        if (response.status === 'Failed') {
          throw new Error(response.message);
        }
        return response;
      }),
    );

    const succeeded = results.filter((result) => result.status === 'fulfilled');
    const failed = results.filter((result) => result.status === 'rejected');

    // Always invalidate cache if any refunds succeeded, since data has changed
    if (succeeded.length > 0) {
      dispatch(paylinkApi.util.invalidateTags(['Paylink']));
    }

    // Handle errors after cache invalidation
    if (failed.length > 0) {
      let errorMessage = 'Something went wrong while refunding the Paylink, please try again later';
      if (refunds.length > 1) {
        if (succeeded.length > 0) {
          errorMessage = `${succeeded.length} refund(s) succeeded, but ${failed.length} failed. Please try again for the failed transactions.`;
        } else {
          errorMessage =
            'Something went wrong while refunding transactions, please try again later';
        }
      }

      // Check if any failed reason contains the partial amount error
      const hasPartialAmountError = failed.some(
        (result) =>
          result.status === 'rejected' &&
          result.reason &&
          isServerError(result.reason) &&
          result.reason.data.message.includes('invalid partial amount'),
      );

      if (hasPartialAmountError) {
        errorMessage = 'Invalid refund amount. Please try again with a lower amount.';
      }

      toast.error(errorMessage);
      throw new Error(errorMessage);
    }

    toast.success('Paylink successfully refunded.');
  };

  return {
    onRefundPaylink,
    isLoading,
  };
};
